/**
 * 登录态的本地持久化
 * ---------------------------------------------------------------------------
 * 单独抽一层的原因是「存哪里」以后一定会变：
 *   - 浏览器阶段：localStorage，刷新页面不用重新登录。
 *   - 桌面端阶段：换成 Electron 的 safeStorage（系统钥匙串加密），
 *     届时只要改这个文件的四个函数，业务代码一行都不用动。
 *
 * 这里只存"够用的最小集合"：token、过期时间、当前门店。
 * 用户资料和权限每次启动都重新调 GET /me 拿，避免本地缓存的权限过期后越权显示按钮。
 */

const STORAGE_KEY = 'toni.console.session';

export type StoredSession = {
  accessToken: string;
  /** 毫秒时间戳。到点前端主动清理，不用等接口报 401 */
  expiresAt: number;
  /** 登录时填的商户编码，下次登录自动回填，减少门店重复输入 */
  merchantCode: string;
  /**
   * 登录人姓名。GET /me 不返回这个字段，只有登录接口给，
   * 所以刷新页面后要靠本地这份来渲染右上角头像，不然会变成空白。
   */
  displayName: string;
  /** 当前选中的门店 ID。多门店时用户可切换，单店就是唯一那家 */
  storeId: string | null;
};

export function readSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as StoredSession;
    if (!session.accessToken) return null;
    // 已过期的 token 直接当作未登录，省掉一次必然失败的接口请求
    if (session.expiresAt && session.expiresAt < Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return session;
  } catch {
    // 本地数据被改坏时按未登录处理，不要让整个应用崩在启动阶段
    return null;
  }
}

export function writeSession(session: StoredSession): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/** 给 http 层用的快捷读取，避免 http 依赖整个 auth 模块 */
export function readAccessToken(): string | null {
  return readSession()?.accessToken ?? null;
}

/** 更新当前门店（切换门店时调用），其余字段保持不变 */
export function updateStoredStoreId(storeId: string): void {
  const session = readSession();
  if (!session) return;
  writeSession({ ...session, storeId });
}

/**
 * 把后端返回的 "8h" / "30m" / "3600" 这类过期描述换算成毫秒时间戳。
 * 解析不出来时给一个保守的 8 小时，宁可早一点要求重新登录。
 */
export function resolveExpiresAt(expiresIn: string): number {
  const matched = /^(\d+)\s*([smhd]?)$/.exec(expiresIn.trim());
  if (!matched) return Date.now() + 8 * 60 * 60 * 1000;
  const amount = Number(matched[1]);
  const unitMs: Record<string, number> = {
    s: 1000,
    m: 60 * 1000,
    h: 60 * 60 * 1000,
    d: 24 * 60 * 60 * 1000,
    '': 1000, // 无单位时后端按秒计
  };
  return Date.now() + amount * unitMs[matched[2]];
}
